import { apiDatasetsPath } from '@/utils/constants.tsx';

import { fetcher } from '../data/fetcher';

export const FORMATS = [
  { value: 'csv', label: 'CSV' },
  { value: 'jsonl', label: 'JSONL' },
];

export type TFormat = (typeof FORMATS)[number]['value'];

export async function download(
  tenantId: string,
  datasetId: string,
  format: TFormat,
  asOf?: string
) {
  if (!datasetId) throw new Error('No dataset ID found');
  if (!format) throw new Error('No format found');
  const res = await fetcher<Response>({
    url: `${apiDatasetsPath}/${datasetId}/${format}${
      asOf ? `?as_of=${encodeURIComponent(asOf)}` : ''
    }`,
    method: 'GET',
    resolveWithResponse: true,
    headers: { 'X-Tenant-Id': tenantId },
  });
  if (!res.ok) {
    let message = '';
    try {
      const body = await res.json();
      message = body?.detail || body?.message || '';
    } catch (e) {
      message = res.statusText;
    }

    throw new Error(`Error fetching dataset: ${message}`);
  }
  const headers = res.headers;
  const blob = await res.blob();

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download =
    headers.get('Content-Disposition')?.split('=')[1] || `dataset.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadDatasetSessionsCSV(
  tenantId: string,
  datasetId: string,
  sessionIds: string[]
): Promise<number> {
  if (!datasetId) throw new Error('No dataset ID found');
  if (!sessionIds?.length) throw new Error('No session IDs provided');

  const response = await fetcher<Response, never, { session_ids: string[] }>({
    url: `${apiDatasetsPath}/${datasetId}/runs?format=csv`,
    method: 'POST',
    json: { session_ids: sessionIds },
    headers: {
      'X-Tenant-Id': tenantId,
    },
    resolveWithResponse: true,
  });

  if (!response.ok) {
    let message = '';
    try {
      const body = await response.json();
      message = body?.detail || body?.message || '';
    } catch (e) {
      message = response.statusText;
    }
    throw new Error(`Error downloading dataset sessions: ${message}`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const filename =
    response.headers
      .get('content-disposition')
      ?.split('filename=')[1]
      .replace(/"/g, '') || 'dataset_sessions';
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  return response.status;
}
